import * as z from 'zod';

import { COUNTRY_CODE_US, type CountryCode, countryCodes } from './geo.ts';

const BaseAddressSchema = z.object({
  street: z.string().min(1),
  street2: z.string().nullable().optional(),
  city: z.string().min(1),
});

type NonUSCountryCode = Exclude<CountryCode, typeof COUNTRY_CODE_US>;

// The runtime filter AND the type must exclude US. Otherwise the two
// discriminated-union members overlap on `country`, and narrowing on
// `address.country === COUNTRY_CODE_US` silently stops working (no error,
// state/postalCode just stay optional).
const nonUSCountryCodes = countryCodes.filter(
  (code): code is NonUSCountryCode => code !== COUNTRY_CODE_US,
) as [NonUSCountryCode, ...NonUSCountryCode[]];

const USAddressSchema = BaseAddressSchema.extend({
  country: z.literal(COUNTRY_CODE_US),
  state: z.string().min(1, 'State is required for US addresses'),
  postalCode: z
    .string()
    .min(5, 'Postal code must be at least 5 characters for US addresses'),
});

const NonUSAddressSchema = BaseAddressSchema.extend({
  country: z.enum(nonUSCountryCodes),
  state: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
});

export const AddressSchema = z.discriminatedUnion(
  'country',
  [USAddressSchema, NonUSAddressSchema],
  // Friendly error for discriminator-mismatch, otherwise emits the full ~250-code list
  { error: () => 'Invalid country code' },
);

export type AddressInput = z.infer<typeof AddressSchema>;
